/**
 * localStorage | sessionStorage | cookie 操作工具

 * 用法:
    // 引入
    import { storage, cookie } from '~/common/utils/storage';

    1. localStorage和sessionStorage的区分只需多一个参数指定是否sessionStorage
      storage.set('userName', '张三');                  // 存储localStorage,支持json格式和字符串
      storage.set('user', { name: '张三' }, true);      // 存储sessionStorage, 支持json格式和字符串

      storage.get('userName');       // 获取localStorage
      storage.get('userName', true); // 获取sessionStorage

      storage.rm('userName');        // 移除localStorage, 支持移除所有all
      storage.rm('all');             // 移除localStorage所有项
      storage.rm('userName', true);  // 移除sessionStorage

      storage.update('user', { age: 6 });         // 更新localStorage，仅支持Object|Array，免去先取出，合并后，再存入的步骤
      storage.update('user', { age: 6 }, true);   // 更新sessionStorage

    2. cookie设置比较特殊
      cookie.set('lang', '支持中文哦');            // 设置cookie,支持String|Object|Array
      cookie.set('buyCount', 20, 60 * 60 * 1000); // 设置cookie有效期一小时
      cookie.get('lang');   // 获取cookie
      cookie.rm('lang');    // 删除cookie
      cookie.update('user', { age: 6 });   // 更新cookie，仅支持储存值为Object|Array的情况

  * Tips:
      1.nuxt服务端不支持localStorage | sessionStorage | cookie
      2.浏览器开启了无痕模式也无法使用
  * Author LGS-20200918
 */

/**
 * 合并对象或数组
 * @param {*} oldObj 
 * @param {*} newObj 
 * @param {*} deep
 */
const extend = (oldObj, newObj, deep) => {
  let value = null;
  if (oldObj instanceof Array) {
    value = oldObj.concat(newObj);
  } else {
    for (const p in newObj) {
      if (newObj.hasOwnProperty(p) && (!oldObj.hasOwnProperty(p) || deep)) {
        oldObj[p] = newObj[p];
      }
    };
    value = oldObj;
  };
  return value;
};

/**
  @param item   itemName
  @param val    string|object
  @param isSession   sessionStorage or localStorage
*/
const storageSave = (item, val, isSession = false) => {
  if (isSession) {
    try {
      sessionStorage.removeItem(item);
      // 存在对象里，能保留原来值的数据类型
      sessionStorage[item] = JSON.stringify({ data: val });
    } catch (error) {
      console.error(error);
    }
  } else {
    try {
      localStorage.removeItem(item);
      localStorage[item] = JSON.stringify({ data: val });
    } catch (error) {
      console.error(error);
    }
  }
};

/**
  @param item   itemName
  @param isSession   sessionStorage or localStorage
*/
const storageGet = (item, isSession = false) => {
  let value = null;
  if (isSession) {
    try {
      value = JSON.parse(sessionStorage[item]);
    } catch (error) {
      if (typeof window !== 'undefined'&& window.sessionStorage && sessionStorage[item]) {
        value = { data: sessionStorage[item] };
      } else{
        value = null;
      }
    }
  } else {
    try {
      value = JSON.parse(localStorage[item]);
    } catch (error) {
      if (typeof window !== 'undefined'&& window.sessionStorage && localStorage[item]) {
        value = { data: localStorage[item] };
      } else {
        value = null;
      }
    }
  };
  if (value !== null && typeof value === 'object' && typeof value.data !== 'undefined') {
    return value.data;
  } else {
    return value;
  }
};

/**
  @param item   clear all or removeItem
  @param isSession   sessionStorage or localStorage
*/
const storageRemove = (item = 'all', isSession = false) => {
  if (isSession) {
    if (item === 'all') {
      try {
        sessionStorage.clear();
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        sessionStorage.removeItem(item);
      } catch (error) {
        console.error(error);
      }
    }
  } else {
    if (item === 'all') {
      try {
        localStorage.clear();
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        localStorage.removeItem(item);
      } catch (error) {
        console.error(error);
      }
    }
  }
};

/**
  *update for object item
  @param item   clear all or removeItem
  @param isSession   sessionStorage or localStorage
*/
const storageUpdate = (item, addObj, isSession = false) => {
  if (storageGet(item, isSession) === null) {
    storageSave(item, addObj, isSession);
  } else {
    const origin = storageGet(item, isSession);
    const newVal = extend(origin, addObj, true);
    storageSave(item, newVal, isSession);
  }
};

/**
  *save cookie
  * @param item key
  * @param val  val
  * @param expires  有效期毫秒 默认不设置，关闭浏览器即失效
  * @param path  保存路径，默认根目录
*/
const cookieSave = (item, val, expires, path = '/') => {
  if(!process.client) return
  const isiOS = !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);

  let value = (typeof val === 'object') ? JSON.stringify(val) : val;
  if (isiOS) {
    // ios不能设置中文cookie
    value = encodeURIComponent(value);
  };

  let cookie = `${item}=${value}`;

  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires);
    cookie += `;expires=${date.toUTCString()}`;
  }

  cookie += `;path=${path}`;

  window.document.cookie = cookie;
};

/**
  @param item   cookie name
*/
const cookieGet = (item) => {
  if(!process.client) return
  const isiOS = !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
  const reg = new RegExp(`(^| )${item}=([^;]*)(;|$)`);
  const cookieMatch = document.cookie.match(reg);
  if (cookieMatch && cookieMatch[2]) {
    let value = cookieMatch[2];
    if (isiOS) {
      value = decodeURIComponent(value);
    }

    if (value.indexOf('{') === 0 || value.indexOf('[') === 0) {
      return JSON.parse(value);
    }
    return value;
  } else {
    return null;
  }
};

/**
  @param item  remove cookie item
*/
const cookieRemove = (item) => {
  const exp = new Date();
  exp.setTime(exp.getTime() - 1);
  // 一定要设置path啊
  window.document.cookie = `${item}='';expires=${exp.toGMTString()};path=/`;
};

/**
  *update for object item
  @param item   clear all or removeItem
*/
const cookieUpdate = (item, addObj) => {
  if (!item || item.indexOf('{') !== 0 || item.indexOf('[') !== 0) return;

  const origin = cookieGet(item);
  const newVal = extend(JSON.parse(origin), addObj, true);
  cookieSave(item, JSON.stringify(newVal));
};

export const storage = {
  set: storageSave,
  get: storageGet,
  rm: storageRemove,
  update: storageUpdate
};

export const cookie = {
  set: cookieSave,
  get: cookieGet,
  rm: cookieRemove,
  update: cookieUpdate
};
